// frontend/src/components/ImageUpload.js
import React, { useEffect, useRef, useState } from "react";

function ImageUpload({ 
  currentImage, 
  onUploadComplete, 
  buttonText = "Upload Image",
  multiple = false,
  maxFiles = 5,
  currentImages = [],
  onRemove
}) {
  const [uploading, setUploading] = useState(false);
  const [previewUrls, setPreviewUrls] = useState(currentImages.length ? currentImages : (currentImage ? [currentImage] : []));
  const inputRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const removePreview = (index) => {
    if (uploading) return;
    setPreviewUrls(urls => urls.filter((_, previewIndex) => previewIndex !== index));
    onRemove?.(index);
  };

  // The parent owns the saved URLs. Mirror them here so a successful upload
  // always has a visible result, including the multiple-image order form.
  useEffect(() => {
    setPreviewUrls(currentImages.length ? currentImages : (currentImage ? [currentImage] : []));
  }, [currentImage, currentImages]);

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    // If multiple mode, handle multiple files
    if (multiple) {
      const fileArray = Array.from(files);
      
      // Check if adding these would exceed maxFiles
      if (currentImages.length + fileArray.length > maxFiles) {
        alert(`You can only upload up to ${maxFiles} images. You already have ${currentImages.length}.`);
        return;
      }
      
      // Validate each file
      for (const file of fileArray) {
        if (!file.type.startsWith('image/')) {
          alert(`"${file.name}" is not an image file.`);
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          alert(`"${file.name}" is larger than 5MB.`);
          return;
        }
      }
      
      // Show the selected files immediately. The cloud URLs replace these
      // local previews once the upload completes.
      const localPreviews = fileArray.map(file => URL.createObjectURL(file));
      setPreviewUrls([...currentImages, ...localPreviews]);
      setUploading(true);
      
      try {
        const uploadedUrls = await Promise.all(fileArray.map(async (file) => {
          const formData = new FormData();
          formData.append("image", file);
          
          const token = localStorage.getItem("erp_token");
          const res = await fetch(`${API_URL}/api/upload/image`, {
            method: "POST",
            headers: { "x-auth-token": token },
            body: formData
          });
          
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "Upload failed");
          return data.url;
        }));
        
        if (onUploadComplete) {
          // If currentImages is provided, append to existing
          if (currentImages.length > 0) {
            onUploadComplete([...currentImages, ...uploadedUrls]);
          } else {
            onUploadComplete(uploadedUrls);
          }
        }
        
      } catch (err) {
        console.error("Upload error:", err);
        setPreviewUrls(currentImages);
        alert("Upload failed: " + err.message);
      } finally {
        setUploading(false);
        e.target.value = "";
      }
      
      return;
    }
    
    // Single file upload
    const file = files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert("Please select an image file (JPEG, PNG, GIF, WEBP)");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be less than 5MB");
      return;
    }
    
    const localPreview = URL.createObjectURL(file);
    setPreviewUrls([localPreview]);
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("image", file);
      
      const token = localStorage.getItem("erp_token");
      const res = await fetch(`${API_URL}/api/upload/image`, {
        method: "POST",
        headers: { "x-auth-token": token },
        body: formData
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Upload failed");
      
      setPreviewUrls([data.url]);
      if (onUploadComplete) onUploadComplete(data.url);
      
    } catch (err) {
      console.error("Upload error:", err);
      setPreviewUrls(currentImage ? [currentImage] : []);
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  
  const buttonStyle = {
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    cursor: uploading ? "not-allowed" : "pointer",
    fontSize: 13,
    fontWeight: 600,
    background: "#6366f1",
    color: "#fff",
    transition: "opacity 0.15s",
    textAlign: "center",
    opacity: uploading ? 0.6 : 1,
    display: "inline-block"
  };
  
  const inputProps = multiple 
    ? { type: "file", accept: "image/jpeg,image/png,image/gif,image/webp", multiple: true, onChange: handleFileSelect, style: { display: "none" }, disabled: uploading }
    : { type: "file", accept: "image/jpeg,image/png,image/gif,image/webp", onChange: handleFileSelect, style: { display: "none" }, disabled: uploading };
  
  return (
    <div>
      <input ref={inputRef} {...inputProps} />
      <button
        type="button"
        style={buttonStyle}
        disabled={uploading}
        onClick={(event) => {
          // This component is reused inside forms. Explicitly preventing the
          // default keeps choosing a file from submitting/reloading its form.
          event.preventDefault();
          inputRef.current?.click();
        }}
      >
        {uploading ? "Uploading..." : buttonText}
      </button>
      {previewUrls.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }} aria-live="polite">
          {previewUrls.map((url, index) => (
            <div key={`${url}-${index}`} style={{ position: "relative", width: 76, height: 76, borderRadius: 8, overflow: "hidden", border: "1px solid var(--border)", background: "var(--surface2)" }}>
              <img src={url} alt={`Uploaded product ${index + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              {onRemove && (
                <button
                  type="button"
                  aria-label={`Remove selected image ${index + 1}`}
                  title="Remove image"
                  disabled={uploading}
                  onClick={() => removePreview(index)}
                  style={{ position: "absolute", top: 3, right: 3, width: 21, height: 21, padding: 0, border: "none", borderRadius: "50%", background: "rgba(0,0,0,.72)", color: "#fff", cursor: uploading ? "not-allowed" : "pointer", fontSize: 16, lineHeight: "20px", fontWeight: 700 }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ImageUpload;